/**
 * Pezzi comuni delle statistiche (V32–V34): la pagina con le tre schede nel
 * componente Tabs e la riga dei filtri, le card su sfondo grigio, i riquadri
 * dei numeri e il confronto fra due eventi gemelli. La maglia del giocatore
 * è quella comune a tutte le liste (`comuni/Maglia.tsx`).
 *
 * Il filtro per competizione vive nella query string e segue chi passa da
 * una scheda all'altra; le presenze non lo hanno.
 */

import type { ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { Corpo } from '../comuni/Corpo.js';
import { Filtro, TUTTI } from '../comuni/FiltriVista.js';
import { Testata } from '../comuni/Testata.js';
import { type Competizione, competizioneDaSearch, queryCompetizione } from './statistiche.js';
import { Nota } from '../comuni/Nota.js';

const SCHEDE = [
  ['squadra', '', 'Squadra', true],
  ['giocatori', '/giocatori', 'Giocatori', true],
  ['presenze', '/presenze', 'Presenze', false],
] as const;

export function PaginaStatistiche({
  ss,
  scheda,
  competizioni,
  filtri,
  children,
}: {
  ss: string;
  scheda: (typeof SCHEDE)[number][0];
  /** Assente nelle presenze, che non si filtrano per competizione. */
  competizioni?: Competizione[];
  /** Altri filtri della scheda, dopo la competizione. */
  filtri?: ReactNode;
  children: ReactNode;
}) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const competizione = competizioneDaSearch(search);
  const base = `/sq/${ss}/statistiche`;

  return (
    <div>
      <Testata titolo="Statistiche" voce="statistiche" />
      <Corpo className="flex flex-col gap-4">
        {/* Le schede si sostituiscono nella cronologia, come nella scheda giocatore. */}
        <Tabs value={scheda}>
          <TabsList className="w-full justify-start overflow-x-auto [scrollbar-width:none]">
            {SCHEDE.map(([valore, coda, etichetta, conFiltro]) => (
              <TabsTrigger key={valore} value={valore} asChild>
                <NavLink
                  to={`${base}${coda}${conFiltro ? queryCompetizione(competizione) : ''}`}
                  end
                  replace
                >
                  {etichetta}
                </NavLink>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {(competizioni || filtri) && (
          <div className="flex flex-wrap items-center gap-3">
            {competizioni && (
              <Filtro
                etichetta="Competizione"
                valore={competizione ?? TUTTI}
                onCambia={(v) =>
                  navigate(`${pathname}${queryCompetizione(v === TUTTI ? undefined : v)}`, {
                    replace: true,
                  })
                }
                opzioni={[
                  { valore: TUTTI, etichetta: 'Tutte' },
                  ...competizioni.map((c) => ({ valore: c.id, etichetta: c.nome })),
                ]}
              />
            )}
            {filtri}
          </div>
        )}

        {children}
      </Corpo>
    </div>
  );
}

/**
 * Una card su sfondo grigio con il titolo in maiuscolo al centro, come i
 * gruppi degli elenchi: l'icona a sinistra, il conteggio in un badge a destra.
 */
export function Sezione({
  titolo,
  icona,
  conteggio,
  nota,
  className,
  children,
}: {
  titolo: string;
  icona?: ReactNode;
  conteggio?: number;
  nota?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card className={cn('h-full bg-muted', className)}>
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          {icona}
          <h2>{titolo}</h2>
          {conteggio != null && <Badge className="tabular-nums">{conteggio}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-2 @xl:px-(--card-spacing)">
        {children}
        {nota && <Nota className="px-1">{nota}</Nota>}
      </CardContent>
    </Card>
  );
}

/** Un numero con la sua etichetta e l'icona, e sotto una riga di contorno. */
export function Riquadro({
  etichetta,
  icona,
  valore,
  sotto,
}: {
  etichetta: string;
  icona?: ReactNode;
  valore: ReactNode;
  sotto?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-lg border bg-card p-3">
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
        {icona}
        <span className="truncate">{etichetta}</span>
      </span>
      <span className="text-2xl leading-8 font-bold tabular-nums">{valore}</span>
      {sotto && <span className="text-sm text-muted-foreground tabular-nums">{sotto}</span>}
    </div>
  );
}

/**
 * Due eventi gemelli a confronto: la quota del primo sul totale e una barra
 * divisa nei colori dei due, verde cio che guadagna e rosso cio che perde;
 * per i cartellini, giallo e rosso sotto il totale.
 */
export function Confronto({
  etichetta,
  icona,
  quota,
  valore,
  a,
  b,
}: {
  etichetta: string;
  icona?: ReactNode;
  quota: number | null;
  /** Al posto della quota in grande: il totale, come nei cartellini. */
  valore?: ReactNode;
  a: { etichetta: string; valore: number; colore: string };
  b: { etichetta: string; valore: number; colore: string };
}) {
  return (
    <Riquadro
      etichetta={etichetta}
      icona={icona}
      valore={valore ?? (quota == null ? '—' : `${quota.toLocaleString('it-IT')}%`)}
      sotto={
        <span className="flex flex-col gap-1.5">
          <span className="flex h-2 overflow-hidden rounded-full bg-muted">
            {quota != null && (
              <>
                <span className={a.colore} style={{ width: `${quota}%` }} />
                <span className={b.colore} style={{ width: `${100 - quota}%` }} />
              </>
            )}
          </span>
          <span className="flex justify-between gap-2">
            <span>
              {a.valore.toLocaleString('it-IT')} {a.etichetta}
            </span>
            <span>
              {b.valore.toLocaleString('it-IT')} {b.etichetta}
            </span>
          </span>
        </span>
      }
    />
  );
}

/**
 * Il tassello a destra della riga: il numero per cui l'elenco e ordinato,
 * con la sua icona sulla fascia, colorata quando il valore ha una fascia.
 */
export function Tassello({
  icona,
  valore,
  colore,
  titolo,
}: {
  icona: ReactNode;
  valore: ReactNode;
  colore?: string;
  titolo: string;
}) {
  return (
    <span
      className="flex w-14 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-background text-center"
      title={titolo}
    >
      <span className={cn('flex justify-center py-0.5', colore ?? 'bg-muted')}>{icona}</span>
      <span className="py-1 text-lg leading-6 font-bold tabular-nums">{valore}</span>
    </span>
  );
}

/** La riga di un giocatore, che apre la sua scheda. */
export function RigaGiocatore({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Link
      to={`/giocatori/${id}`}
      // Alta quanto la riga della griglia: su due colonne le vicine si allineano.
      className="flex h-full items-center gap-3 rounded-lg border border-border bg-card p-2.5"
    >
      {children}
    </Link>
  );
}

/** Un numero con la sua icona, nella riga di un giocatore. */
export function Voce({
  icona,
  valore,
  titolo,
}: {
  icona: ReactNode;
  valore: ReactNode;
  titolo: string;
}) {
  return (
    <span className="flex items-center gap-1" title={titolo}>
      {icona}
      <span className="font-medium text-foreground tabular-nums">{valore}</span>
    </span>
  );
}
